import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, Search, PlusCircle } from "lucide-react";
import { StatusBadge } from "./Dashboard";
import { toast } from "sonner";

export default function Webinars() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [myEnrolled, setMyEnrolled] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [mine, setMine] = useState(user?.role === "teacher");

  const load = () => {
    const params = { q, category };
    if (user?.role === "teacher") params.mine = mine;
    axios.get(`${API}/webinars`, { params, withCredentials: true }).then((r) => setItems(r.data));
    if (user?.role === "learner") {
      axios.get(`${API}/my/webinars`, { withCredentials: true }).then((r) => setMyEnrolled(r.data));
    }
  };

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true }).then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, category, mine, user?.role]);

  const isLearner = user?.role === "learner";
  const isTeacher = user?.role === "teacher";
  const enrolledIds = new Set(myEnrolled.map((e) => e.webinar_id));

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Live sessions</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">Webinars</h1>
        </div>
        {isTeacher && user?.teacher_status === "approved" && (
          <button
            onClick={() => navigate("/teacher/new-webinar")}
            className="btn-primary flex items-center gap-2"
            data-testid="new-webinar-btn"
          >
            <PlusCircle size={16} /> Plan a webinar
          </button>
        )}
      </div>

      {isLearner && (
        <div className="grid md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-6 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
            <input
              data-testid="webinar-search-input"
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-[#E5E3D8] bg-white focus:outline-none focus:border-[#0A231C]"
              placeholder="Search webinars..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="md:col-span-6 flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("all")}
              data-testid="wcat-all"
              className={`text-sm px-4 py-2 rounded-full border ${category === "all" ? "bg-[#0A231C] text-white border-[#0A231C]" : "border-[#E5E3D8] text-[#1C1C1C]"}`}
            >All</button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`wcat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm px-4 py-2 rounded-full border ${category === cat ? "bg-[#0A231C] text-white border-[#0A231C]" : "border-[#E5E3D8] text-[#1C1C1C]"}`}
              >{cat}</button>
            ))}
          </div>
        </div>
      )}

      {isTeacher && (
        <div className="flex gap-2">
          <button onClick={() => setMine(true)} className={`text-sm px-4 py-2 rounded-full border ${mine ? "bg-[#0A231C] text-white border-[#0A231C]" : "border-[#E5E3D8]"}`} data-testid="teacher-mine-toggle">My webinars</button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#5C5C5C]" data-testid="no-webinars">
          {isLearner ? "No webinars available yet." : isTeacher ? "You haven't planned a webinar yet." : "No webinars in the system."}
        </div>
      ) : isLearner ? (
        <div className="grid md:grid-cols-3 gap-6" data-testid="webinars-grid">
          {items.map((w) => <LearnerWebinarCard key={w.id} w={w} enrolled={enrolledIds.has(w.id)} />)}
        </div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="lms-table w-full">
            <thead>
              <tr>
                <th>Title</th><th>Category</th><th>When</th><th>Price</th><th>Status</th><th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} data-testid={`webinar-row-${w.id}`}>
                  <td className="font-heading font-medium">{w.title}</td>
                  <td>{w.category}</td>
                  <td className="text-xs text-[#5C5C5C]">{new Date(w.scheduled_at).toLocaleString()}</td>
                  <td>{w.price > 0 ? `₹${w.price}` : "Free"}</td>
                  <td><StatusBadge s={w.status || "draft"} /></td>
                  <td className="text-right pr-4">
                    <button onClick={() => navigate(`/webinars/${w.id}`)} className="text-sm text-[#0A231C] hover:underline mr-3">View</button>
                    {isTeacher && (
                      <button onClick={() => navigate(`/teacher/webinars/${w.id}/edit`)} className="text-sm text-[#D45D3C] hover:underline">Edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LearnerWebinarCard({ w, enrolled }) {
  return (
    <Link to={`/webinars/${w.id}`} className="course-card card-surface block" data-testid={`webinar-card-${w.id}`}>
      <div className="h-40 overflow-hidden rounded-t-[14px] bg-[#0A231C]">
        {w.cover_image ? (
          <img src={w.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white">
            <Calendar size={40} />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="overline">{w.category}</div>
          <div className="text-xs text-[#5C5C5C] flex items-center gap-1"><Clock size={12} /> {w.duration_minutes || 60}m</div>
        </div>
        <div className="font-heading font-semibold text-[#0A231C] mt-1 leading-snug">{w.title}</div>
        <div className="mt-2 text-xs text-[#5C5C5C]">by {w.instructor_name}</div>
        <div className="mt-2 text-xs text-[#5C5C5C] flex items-center gap-1">
          <Calendar size={12} /> {new Date(w.scheduled_at).toLocaleString()}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="font-heading font-semibold text-[#0A231C]">{w.price > 0 ? `₹${w.price}` : "Free"}</div>
          {enrolled && <div className="overline text-[#2E7D32]">Registered</div>}
        </div>
      </div>
    </Link>
  );
}
